(() => {
  const STORAGE_KEY = 'kingdomRadioStateV2';
  const PLAYLIST_KEY = 'kingdomRadioPlaylistV2';
  const ACTIVATED_KEY = 'kingdomRadioActivatedV2';

  const init = () => {
    const player = document.getElementById('KingdomRadioPlayer');
    if (!player || player.dataset.ready === 'true') return;
    player.dataset.ready = 'true';

    const audio = player.querySelector('[data-krp-audio]');
    const art = player.querySelector('[data-krp-art]');
    const placeholder = player.querySelector('[data-krp-placeholder]');
    const title = player.querySelector('[data-krp-title]');
    const artist = player.querySelector('[data-krp-artist]');
    const toggle = player.querySelector('[data-krp-toggle]');
    const icon = player.querySelector('[data-krp-play-icon]');
    const current = player.querySelector('[data-krp-current]');
    const duration = player.querySelector('[data-krp-duration]');
    const progress = player.querySelector('[data-krp-progress]');
    const progressTrack = player.querySelector('[data-krp-progress-track]');

    let playlist = [];
    let index = -1;
    let lastSavedSecond = -1;

    const safeParse = (value, fallback) => {
      try { return JSON.parse(value) ?? fallback; } catch (_) { return fallback; }
    };
    const formatTime = (seconds) => {
      if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
      const mins = Math.floor(seconds / 60);
      return `${mins}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
    };
    const getState = () => safeParse(localStorage.getItem(STORAGE_KEY), {});
    const isActivated = () => sessionStorage.getItem(ACTIVATED_KEY) === 'true';
    const activate = () => { try { sessionStorage.setItem(ACTIVATED_KEY, 'true'); } catch (_) {} };
    const savePlaylist = () => {
      try { localStorage.setItem(PLAYLIST_KEY, JSON.stringify(playlist)); } catch (_) {}
    };
    const saveState = (extra = {}) => {
      if (index < 0 || !playlist[index]) return;
      const state = {
        index,
        src: playlist[index].src,
        currentTime: Number.isFinite(audio.currentTime) ? audio.currentTime : 0,
        wasPlaying: !audio.paused && !audio.ended,
        updatedAt: Date.now(),
        ...extra
      };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_) {}
    };
    const updateUI = () => {
      const song = playlist[index];
      if (!song) return;
      const state = getState();
      player.hidden = !isActivated();
      title.textContent = song.title || 'Kingdom Music Radio';
      artist.textContent = song.artist || song.genre || 'Kingdom Music Radio';
      if (song.cover) {
        art.src = song.cover;
        art.alt = song.title ? `${song.title} cover art` : 'Song cover art';
        art.hidden = false;
        placeholder.hidden = true;
      } else {
        art.removeAttribute('src');
        art.hidden = true;
        placeholder.hidden = false;
      }
      const playing = !audio.paused && !audio.ended;
      icon.textContent = playing ? '❚❚' : '▶';
      toggle.setAttribute('aria-label', playing ? 'Pause' : 'Play');
      current.textContent = formatTime(audio.currentTime);
      duration.textContent = formatTime(audio.duration);
      progress.style.width = audio.duration ? `${Math.min(100, (audio.currentTime / audio.duration) * 100)}%` : '0%';
      document.dispatchEvent(new CustomEvent('kingdom-radio:state', { detail: { index, song, playing } }));
    };
    const loadIndex = async (newIndex, { autoplay = true, time = 0 } = {}) => {
      if (!playlist.length) return;
      index = (newIndex + playlist.length) % playlist.length;
      const song = playlist[index];
      if (!song?.src) return;
      if (audio.src !== song.src) audio.src = song.src;
      const setTime = () => {
        if (Number.isFinite(time) && time > 0 && Number.isFinite(audio.duration)) {
          audio.currentTime = Math.min(time, Math.max(0, audio.duration - .25));
        }
      };
      if (audio.readyState >= 1) setTime();
      else audio.addEventListener('loadedmetadata', setTime, { once: true });
      updateUI();
      if (autoplay) {
        try { await audio.play(); } catch (_) { updateUI(); }
      }
      saveState({ wasPlaying: autoplay, currentTime: Number.isFinite(time) ? time : (audio.currentTime || 0) });
    };
    const setPlaylist = (incoming) => {
      if (!Array.isArray(incoming) || !incoming.length) return;
      const previousSrc = playlist[index]?.src || getState().src;
      playlist = incoming.filter((song) => song && song.src);
      savePlaylist();
      if (previousSrc) {
        const matching = playlist.findIndex((song) => song.src === previousSrc);
        if (matching >= 0) index = matching;
      }
    };

    window.KingdomRadio = {
      setPlaylist,
      play: async (requestedIndex) => {
        if (!playlist.length) return;
        const prior = getState();
        activate();
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prior, updatedAt: Date.now() })); } catch (_) {}
        player.hidden = false;
        const requested = Number.isInteger(requestedIndex) ? requestedIndex : (index >= 0 ? index : 0);
        if (requested !== index || !audio.src) await loadIndex(requested, { autoplay: true });
        else { try { await audio.play(); } catch (_) {} }
      },
      pause: () => audio.pause(),
      toggle: async () => {
        if (!playlist.length) return;
        if (index < 0) return loadIndex(0, { autoplay: true });
        if (audio.paused) { try { await audio.play(); } catch (_) {} } else audio.pause();
      },
      next: () => loadIndex(index < 0 ? 0 : index + 1, { autoplay: true }),
      prev: () => loadIndex(index < 0 ? 0 : index - 1, { autoplay: true }),
      getState: () => ({ playlist, index, playing: !audio.paused, song: playlist[index] || null })
    };

    toggle.addEventListener('click', () => window.KingdomRadio.toggle());
    player.querySelector('[data-krp-next]').addEventListener('click', () => window.KingdomRadio.next());
    player.querySelector('[data-krp-prev]').addEventListener('click', () => window.KingdomRadio.prev());
    progressTrack.addEventListener('click', (event) => {
      if (!audio.duration) return;
      const rect = progressTrack.getBoundingClientRect();
      audio.currentTime = Math.max(0, Math.min(audio.duration, ((event.clientX - rect.left) / rect.width) * audio.duration));
    });

    audio.addEventListener('play', () => { updateUI(); saveState({ wasPlaying: true }); });
    audio.addEventListener('pause', () => { updateUI(); saveState({ wasPlaying: false }); });
    audio.addEventListener('loadedmetadata', updateUI);
    audio.addEventListener('timeupdate', () => {
      updateUI();
      const second = Math.floor(audio.currentTime);
      if (second !== lastSavedSecond && second % 3 === 0) {
        lastSavedSecond = second;
        saveState();
      }
    });
    audio.addEventListener('ended', () => window.KingdomRadio.next());
    window.addEventListener('pagehide', () => saveState());

    document.addEventListener('kingdom-radio:playlist', (event) => setPlaylist(event.detail));
    document.addEventListener('kingdom-radio:play-index', (event) => window.KingdomRadio.play(event.detail?.index));

    const storedPlaylist = safeParse(localStorage.getItem(PLAYLIST_KEY), []);
    const state = getState();
    if (storedPlaylist.length && state.src && isActivated()) {
      setPlaylist(storedPlaylist);
      const restoredIndex = playlist.findIndex((song) => song.src === state.src);
      if (restoredIndex >= 0) {
        loadIndex(restoredIndex, { autoplay: false, time: state.currentTime || 0 }).then(async () => {
          if (state.wasPlaying) {
            try { await audio.play(); } catch (_) {
              // Browser autoplay rules may require one tap after a full page navigation.
              updateUI();
            }
          }
        });
      }
    }

    document.dispatchEvent(new CustomEvent('kingdom-radio:ready'));
  };

  // The player markup is rendered before the page sections, so initialize immediately.
  // This guarantees window.KingdomRadio exists before the Kingdom Music section's inline script runs.
  init();
})();
